import { addClickOutsideListener } from "./helpers/dialogHelpers";

const calendar: HTMLElement = document.querySelector(".calendar");

if (calendar) {
    const prevMonthButton: HTMLButtonElement = calendar.querySelector(".change-month-button--prev");
    const nextMonthButton: HTMLButtonElement = calendar.querySelector(".change-month-button--next");
    const prevDayButton: HTMLButtonElement = calendar.querySelector(".change-day-button--prev");
    const nextDayButton: HTMLButtonElement = calendar.querySelector(".change-day-button--next");
    const calendarTodayButton = calendar.querySelector(".today-button");
    const calendarMonthsHeader: NodeListOf<HTMLElement> = calendar.querySelectorAll(".active-month-title .month");
    const calendarMonths: NodeListOf<HTMLElement> = calendar.querySelectorAll(".calendar-month");
    const calendarTodayView = calendar.querySelector(".calendar-month--today");
    const calendarDayTodayView: HTMLElement = calendar.querySelector(".calendar-day--today");
    const calendarTodayViewIndex: number = parseInt(calendarTodayView.getAttribute("data-index-month"));
    const calendarEvents: NodeListOf<HTMLElement> = calendar.querySelectorAll(".event");
    const calendarDays: NodeListOf<HTMLElement> = calendar.querySelectorAll(".calendar-month .calendar-day");
    const calendarDayMobileWrapper: HTMLButtonElement = calendar.querySelector(".calendar-day-mobile");
    const filterButtons = calendar.querySelectorAll(".filter .tag");
    const totalMonths: number = calendarMonths.length; // amount of displayed months
    /* activeCalendar & filters (if enabled) */
    let activeCalendar = calendarTodayView;
    let activeCalendarMonthIndex: number = parseInt(calendarTodayView.getAttribute("data-index-month"));
    let maxLaptopScreenWidth: MediaQueryList = window.matchMedia("(max-width: 992px)");
    let activeTags: String[] = [];
    /* classnames */
    const activeCalendarClass: string = "active";
    const selectedTagClass: string = "selected";
    const filteredTagClass: string = "filtered";

    /* toggle calendar header + main view */
    const toggleCalendarState = () => {
        activeCalendar.classList.toggle(activeCalendarClass);
        calendarMonthsHeader[activeCalendarMonthIndex].classList.toggle(activeCalendarClass);
    };

    /* set prev + next buttons disabled state for first & last day in month view */
    const checkPrevNextMonthButtonState = () => {
        prevMonthButton.disabled = activeCalendarMonthIndex == 0;
        nextMonthButton.disabled = activeCalendarMonthIndex == totalMonths - 1;
    };

    /* month change functionality (prev/next month button clicked) */
    const handleVisibleMonthChangeButtonClick = (updatedIndex: number) => {
        /* remove old active state */
        toggleCalendarState();

        /* update active index (and check prev/next button disabled status) */
        activeCalendarMonthIndex = updatedIndex;
        activeCalendar = calendarMonths[activeCalendarMonthIndex];

        checkPrevNextMonthButtonState();

        /* set new active state */
        toggleCalendarState();

        // reset active day to first month day (or current day if current month is active)
        const curActiveDay: HTMLElement = calendar.querySelector(".calendar-day.active");
        if (activeCalendar == calendarTodayView) {
            handleDayChange(curActiveDay, calendarDayTodayView);
        } else {
            const firstDayOfCurrentMonth: HTMLElement = activeCalendar.querySelector(
                ".calendar-day:not(.calendar-day--not-current-month)",
            );
            handleDayChange(curActiveDay, firstDayOfCurrentMonth);
        }
    };

    /* Prev/next month nav buttons */
    prevMonthButton.addEventListener("click", () => {
        handleVisibleMonthChangeButtonClick(activeCalendarMonthIndex - 1);
    });

    nextMonthButton.addEventListener("click", () => {
        handleVisibleMonthChangeButtonClick(activeCalendarMonthIndex + 1);
    });

    /* Show current month button */
    calendarTodayButton.addEventListener("click", () => {
        handleVisibleMonthChangeButtonClick(calendarTodayViewIndex);
    });

    /* calendar day change functionality (mobile version) */
    const checkPrevNextDayButtonState = (activeDay: HTMLElement) => {
        if (activeDay) {
            prevDayButton.disabled = activeDay.previousElementSibling == null;
            nextDayButton.disabled = activeDay.nextElementSibling == null;
        }
    };

    /* Set today's day active */
    const setTodayDayActive = (scrollIntoView: boolean = true) => {
        const curActiveDay: HTMLElement = calendar.querySelector(".calendar-day.active");
        handleDayChange(curActiveDay, calendarDayTodayView, scrollIntoView);
    };

    /* Mobile: Change currently active day */
    const handleDayChange = (
        currentActiveChild: HTMLElement,
        newActiveChild: HTMLElement,
        scrollIntoView: boolean = true,
    ) => {
        // remove old active day content
        const curActiveDay = calendarDayMobileWrapper.querySelector(".calendar-day");
        if (curActiveDay) {
            const clonedCalendarDayButtons = curActiveDay.querySelectorAll(".event");
            clonedCalendarDayButtons.forEach((button: HTMLButtonElement) => {
                const newButton = button.cloneNode(true);
                button.parentNode.replaceChild(newButton, button);
            });
            curActiveDay.remove();
        }

        // clone new active day
        const clonedCalendarDay: HTMLElement = newActiveChild.cloneNode(true) as HTMLElement;
        calendarDayMobileWrapper.prepend(clonedCalendarDay);
        // add overlay open / event listeners to event buttons inside the cloned day element
        const clonedCalendarDayButtons = clonedCalendarDay.querySelectorAll(".event");
        clonedCalendarDayButtons.forEach((button: HTMLButtonElement) => {
            addOverlayClickListener(button);
        });

        // change active state inside calendar view
        currentActiveChild.classList.toggle(activeCalendarClass);
        newActiveChild.classList.toggle(activeCalendarClass);

        // scroll calendar day into view if not fully visible
        if (
            scrollIntoView &&
            maxLaptopScreenWidth.matches &&
            calendar.getBoundingClientRect().bottom > window.innerHeight
        ) {
            calendar.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
        }

        checkPrevNextDayButtonState(newActiveChild);
    };

    prevDayButton.addEventListener("click", () => {
        const curActiveDay: HTMLElement = calendar.querySelector(".calendar-day.active");
        handleDayChange(curActiveDay, curActiveDay.previousElementSibling as HTMLElement); // todo prevent 'as HTMLElement'
    });

    nextDayButton.addEventListener("click", () => {
        const curActiveDay: HTMLElement = calendar.querySelector(".calendar-day.active");
        handleDayChange(curActiveDay, curActiveDay.nextElementSibling as HTMLElement); // todo prevent 'as HTMLElement'
    });

    calendarDays.forEach((calendarDay: HTMLElement) => {
        const dayChangeButton: HTMLButtonElement = calendarDay.querySelector(".calendar-day-mobile-button");

        dayChangeButton.addEventListener("click", () => {
            const curActiveDay: HTMLElement = calendar.querySelector(".calendar-day.active");

            handleDayChange(curActiveDay, calendarDay);
        });
    });

    /* Tags filter (if filter is selectable / no predefined filter set) */
    filterButtons.forEach((filterButton: HTMLButtonElement) => {
        const tagValue: string = filterButton.getAttribute("data-tag");
        filterButton.addEventListener("click", () => {
            // if filterButton active -> remove from active filters Array
            if (filterButton.classList.contains(selectedTagClass)) {
                const indexToRemove: number = activeTags.indexOf(tagValue);
                activeTags.splice(indexToRemove, 1);
            } else {
                activeTags.push(tagValue);
            }
            filterButton.classList.toggle(selectedTagClass);

            // check events for tags
            calendarEvents.forEach((calendarEvent: HTMLElement) => {
                if (activeTags.length > 0) {
                    const itemFilterValue = calendarEvent.getAttribute("data-filter");
                    const filterTags: string[] = itemFilterValue.split(" ");
                    const hasMatchingTag: boolean = activeTags.some((tag: string) => filterTags.includes(tag));
                    if (!hasMatchingTag) {
                        calendarEvent.classList.add(filteredTagClass);
                        return;
                    }
                }
                calendarEvent.classList.remove(filteredTagClass);
            });
        });
    });

    /* Calc single calendar column (used to style multi day events) */
    const calcCalendarColGrid = () => {
        calendar.style.setProperty("--grid-col-width", `${calendarDays[0].clientWidth}px`);
    };

    document.addEventListener("DOMContentLoaded", () => {
        setTimeout(() => {
            calcCalendarColGrid();
        }, 500);
    });

    window.addEventListener("resize", () => {
        calcCalendarColGrid();
    });

    /* add hover for multiDay events that break to the next row in the layout */
    let activeMultiDayEventButtons: NodeListOf<HTMLElement> = null;
    const setMultiDayEventHoverState = (eventId: string, setActive: boolean) => {
        activeMultiDayEventButtons = activeCalendar.querySelectorAll(`.event[data-event-id="${eventId}"]`);
        activeMultiDayEventButtons.forEach((activeEventButton) => {
            if (setActive) {
                activeEventButton.classList.add("event--hover");
            } else {
                activeEventButton.classList.remove("event--hover");
            }
        });

        if (!setActive) {
            activeMultiDayEventButtons = null;
        }
    };

    const addOverlayClickListener = (calendarEvent: HTMLElement) => {
        const eventId = calendarEvent.getAttribute("data-event-id");
        const overlayDialog: HTMLDialogElement = calendar.querySelector(`.overlay[data-event-id="${eventId}"]`);
        const closeButton = overlayDialog.querySelector(".close-button");

        closeButton.addEventListener("click", () => {
            overlayDialog.close();
        });

        calendarEvent.addEventListener("click", () => {
            overlayDialog.showModal();
        });

        addClickOutsideListener(overlayDialog);
    };

    // initial active day
    setTodayDayActive(false);

    /* Event click -> show overlay & check multirow events hover */
    calendarEvents.forEach((calendarEvent: HTMLButtonElement) => {
        addOverlayClickListener(calendarEvent);

        // hover class for multiDay events that break to the next row in the layout */
        if (calendarEvent.classList.contains("event--multi-row")) {
            const eventId = calendarEvent.getAttribute("data-event-id");

            // hover class for multiDay events that break to the next row in the layout */
            calendarEvent.addEventListener("mouseover", () => {
                setMultiDayEventHoverState(eventId, true);
            });
            calendarEvent.addEventListener("mouseleave", () => {
                setMultiDayEventHoverState(eventId, false);
            });
        }
    });
}
